import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";

import { FIELD_CLASS } from "../guardias/guardiasShared";
import { SELLABLE_PROJECTS_FILTER } from "../projects/sellableProjects";
import type { CrmDataProvider } from "../providers/types";
import {
  LEAD_TYPES,
  type LeadSource,
  type LeadType,
  type Project,
  type Sale,
  type WhatsAppAssignMode,
  type WhatsAppBusinessUnit,
  type WhatsAppUnclassifiedRow,
} from "../types";
import { validateClassifyDraft, type ClassifyDraft } from "./unclassifiedRules";

const CHOICES = 500;
const ADVISOR_ROLES: readonly string[] = ["asesora", "lider"];
const CORRETAJE_ARMS: readonly string[] = ["corretaje", "ambos"];
const ASSIGN_MODES: Record<WhatsAppBusinessUnit, WhatsAppAssignMode[]> = {
  bpo: ["advisor", "carousel"],
  corretaje: ["advisor", "holder"],
};
const EMPTY_DRAFT: ClassifyDraft = {
  businessUnit: "bpo",
  projectId: "",
  leadType: "",
  leadSourceId: "",
  assignMode: "advisor",
  salesId: "",
};

const FieldError = ({ id, text }: { id: string; text?: string }) =>
  text ? (
    <p id={id} role="alert" className="text-[15px] text-destructive">
      {text}
    </p>
  ) : null;

/**
 * 'Es lead': BPO or Corretaje. Lead type and origin are always mandatory (validated before
 * submit). BPO adds the project and either a DIRECT assignment to an active advisor who can own
 * leads (name and clave shown) or 'Enviar al carrusel'. Corretaje has no project and no carousel:
 * an advisor of the corretaje arm or 'me la quedo yo'. A known contact is reused by the server.
 * The RPC re-checks everything; its Spanish refusal is shown inline. On success the tray
 * refreshes and a notification links to the lead file (desktop only: mobile has no lead route).
 */
export const ClassifyLeadDialog = ({
  conversationId,
  knownContact = null,
  open,
  onOpenChange,
}: {
  conversationId: Identifier;
  knownContact?: WhatsAppUnclassifiedRow["knownContact"];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ClassifyDraft>(EMPTY_DRAFT);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const projects = useGetList<Project>(
    "projects",
    {
      pagination: { page: 1, perPage: CHOICES },
      sort: { field: "name", order: "ASC" },
      filter: { ...SELLABLE_PROJECTS_FILTER },
    },
    { enabled: open },
  );
  const sources = useGetList<LeadSource>(
    "lead_sources",
    {
      pagination: { page: 1, perPage: CHOICES },
      sort: { field: "name", order: "ASC" },
      filter: {},
    },
    { enabled: open },
  );
  const sales = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: CHOICES },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: open },
  );
  const advisors = (sales.data ?? []).filter(
    (sale) =>
      !sale.disabled &&
      ADVISOR_ROLES.includes(sale.role) &&
      !!sale.advisor_code?.trim() &&
      (draft.businessUnit === "bpo" ||
        CORRETAJE_ARMS.includes(sale.business_arm ?? "")),
  );

  const errors = validateClassifyDraft(draft);
  const set = (patch: Partial<ClassifyDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));
  const text = (key?: string) =>
    key ? translate(`crm.whatsapp.unclassified.errors.${key}`) : undefined;
  const shown = (key: keyof typeof errors) =>
    submitted ? text(errors[key]) : undefined;

  const close = (next: boolean) => {
    if (!next) {
      setDraft(EMPTY_DRAFT);
      setSubmitted(false);
      setFailure(null);
    }
    onOpenChange(next);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0 || saving) return;
    setSaving(true);
    setFailure(null);
    try {
      const result = await dataProvider.classifyWhatsAppLead({
        conversationId,
        businessUnit: draft.businessUnit,
        projectId: draft.businessUnit === "bpo" ? draft.projectId : null,
        leadType: draft.leadType as LeadType,
        leadSourceId: draft.leadSourceId,
        assignMode: draft.assignMode,
        salesId: draft.assignMode === "advisor" ? draft.salesId : undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ["whatsapp"] });
      await queryClient.invalidateQueries({ queryKey: ["deals"] });
      notify(
        <span>
          {translate("crm.whatsapp.unclassified.lead.success")}
          {isMobile ? null : (
            <>
              {" "}
              <Link
                className="font-medium underline"
                to={`/deals/${result.dealId}/show`}
              >
                {translate("crm.whatsapp.unclassified.lead.open")}
              </Link>
            </>
          )}
        </span>,
        { type: "success" },
      );
      close(false);
    } catch (error) {
      setFailure(
        error instanceof Error && error.message
          ? error.message
          : translate("crm.whatsapp.unclassified.lead.error"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {translate("crm.whatsapp.unclassified.lead.title")}
            </DialogTitle>
            <DialogDescription>
              {translate("crm.whatsapp.unclassified.lead.help")}
            </DialogDescription>
          </DialogHeader>

          {knownContact ? (
            <p className="text-[15px] text-muted-foreground">
              {translate("crm.whatsapp.unclassified.lead.from_contact", {
                name:
                  knownContact.name ??
                  translate("crm.whatsapp.unclassified.known_contact_open"),
              })}
            </p>
          ) : null}

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">
              {translate("crm.whatsapp.unclassified.lead.unit")}
            </legend>
            {(["bpo", "corretaje"] as WhatsAppBusinessUnit[]).map((unit) => (
              <label
                key={unit}
                className="flex min-h-11 items-center gap-3 text-base"
              >
                <input
                  type="radio"
                  name="classify-business-unit"
                  className="size-5"
                  checked={draft.businessUnit === unit}
                  onChange={() =>
                    set({
                      businessUnit: unit,
                      projectId: "",
                      assignMode: "advisor",
                      salesId: "",
                    })
                  }
                />
                {translate(`crm.whatsapp.unclassified.lead.unit_${unit}`)}
              </label>
            ))}
          </fieldset>

          {draft.businessUnit === "bpo" ? (
            <div className="space-y-2">
              <Label htmlFor="classify-project">
                {translate("crm.whatsapp.unclassified.lead.project")}
              </Label>
              <select
                id="classify-project"
                className={FIELD_CLASS}
                value={draft.projectId}
                aria-invalid={!!shown("project")}
                onChange={(e) => set({ projectId: e.target.value })}
              >
                <option value="">
                  {translate("crm.whatsapp.unclassified.lead.choose")}
                </option>
                {(projects.data ?? []).map((project) => (
                  <option key={String(project.id)} value={String(project.id)}>
                    {project.name}
                  </option>
                ))}
              </select>
              <FieldError id="classify-project-error" text={shown("project")} />
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="classify-type">
              {translate("crm.whatsapp.unclassified.lead.type")}
            </Label>
            <select
              id="classify-type"
              className={FIELD_CLASS}
              value={draft.leadType}
              aria-invalid={!!shown("type")}
              onChange={(e) => set({ leadType: e.target.value })}
            >
              <option value="">
                {translate("crm.whatsapp.unclassified.lead.choose")}
              </option>
              {LEAD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {translate(`resources.deals.lead_types.${type}`)}
                </option>
              ))}
            </select>
            <FieldError id="classify-type-error" text={shown("type")} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="classify-source">
              {translate("crm.whatsapp.unclassified.lead.source")}
            </Label>
            <select
              id="classify-source"
              className={FIELD_CLASS}
              value={draft.leadSourceId}
              aria-invalid={!!shown("source")}
              onChange={(e) => set({ leadSourceId: e.target.value })}
            >
              <option value="">
                {translate("crm.whatsapp.unclassified.lead.choose")}
              </option>
              {(sources.data ?? []).map((source) => (
                <option key={String(source.id)} value={String(source.id)}>
                  {source.name}
                </option>
              ))}
            </select>
            <FieldError id="classify-source-error" text={shown("source")} />
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">
              {translate("crm.whatsapp.unclassified.lead.assign")}
            </legend>
            {ASSIGN_MODES[draft.businessUnit].map((mode) => (
              <label
                key={mode}
                className="flex min-h-11 items-center gap-3 text-base"
              >
                <input
                  type="radio"
                  name="classify-assign-mode"
                  className="size-5"
                  checked={draft.assignMode === mode}
                  onChange={() => set({ assignMode: mode, salesId: "" })}
                />
                {translate(
                  `crm.whatsapp.unclassified.lead.mode_${mode}${
                    mode === "advisor" && draft.businessUnit === "corretaje"
                      ? "_corretaje"
                      : ""
                  }`,
                )}
              </label>
            ))}
            {draft.assignMode === "advisor" ? (
              <>
                <Label htmlFor="classify-advisor">
                  {translate("crm.whatsapp.unclassified.lead.advisor")}
                </Label>
                <select
                  id="classify-advisor"
                  className={FIELD_CLASS}
                  value={draft.salesId}
                  aria-invalid={!!shown("advisor")}
                  onChange={(e) => set({ salesId: e.target.value })}
                >
                  <option value="">
                    {translate("crm.whatsapp.unclassified.lead.choose")}
                  </option>
                  {advisors.map((sale) => (
                    <option key={String(sale.id)} value={String(sale.id)}>
                      {`${sale.first_name} ${sale.last_name} (${sale.advisor_code})`}
                    </option>
                  ))}
                </select>
              </>
            ) : draft.assignMode === "carousel" ? (
              <p className="text-[15px] text-muted-foreground">
                {translate("crm.whatsapp.unclassified.lead.carousel_help")}
              </p>
            ) : null}
            <FieldError id="classify-advisor-error" text={shown("advisor")} />
          </fieldset>

          {failure ? (
            <p role="alert" className="text-[15px] text-destructive">
              {failure}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-11"
              onClick={() => close(false)}
            >
              {translate("crm.whatsapp.unclassified.cancel")}
            </Button>
            <Button type="submit" className="h-11" disabled={saving}>
              {translate("crm.whatsapp.unclassified.lead.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
